
/**
 * Adapts type `D` to be usable as a date by cdk-based components that work with dates.
 * @abstract
 */
export abstract class DateAdapter<D> {
/**
 * The locale to use for all dates.
 */
protected locale: any;
/**
 * Gets the year component of the given date.
 * @abstract
 * @param {?} date The date to extract the year from.
 * @return {?} The year component.
 */
getYear(date: D) {}
/**
 * Gets the month component of the given date.
 * @abstract
 * @param {?} date The date to extract the month from.
 * @return {?} The month component (0-indexed, 0 = January).
 */
getMonth(date: D) {}
/**
 * Gets the date of the month component of the given date.
 * @abstract
 * @param {?} date The date to extract the date of the month from.
 * @return {?} The month component (1-indexed, 1 = first of month).
 */
getDate(date: D) {}
/**
 * Gets the day of the week component of the given date.
 * @abstract
 * @param {?} date The date to extract the day of the week from.
 * @return {?} The month component (0-indexed, 0 = Sunday).
 */
getDayOfWeek(date: D) {}
/**
 * Gets a list of names for the months.
 * @abstract
 * @param {?} style The naming style (e.g. long = 'January', short = 'Jan', narrow = 'J').
 * @return {?} An ordered list of all month names, starting with January.
 */
getMonthNames(style: 'long' | 'short' | 'narrow') {}
/**
 * Gets a list of names for the dates of the month.
 * @abstract
 * @return {?} An ordered list of all date of the month names, starting with '1'.
 */
getDateNames() {}
/**
 * Gets a list of names for the days of the week.
 * @abstract
 * @param {?} style The naming style (e.g. long = 'Sunday', short = 'Sun', narrow = 'S').
 * @return {?} An ordered list of all weekday names, starting with Sunday.
 */
getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {}
/**
 * Gets the name for the year of the given date.
 * @abstract
 * @param {?} date The date to get the year name for.
 * @return {?} The name of the given year (e.g. '2017').
 */
getYearName(date: D) {}
/**
 * Gets the first day of the week.
 * @abstract
 * @return {?} The first day of the week (0-indexed, 0 = Sunday).
 */
getFirstDayOfWeek() {}
/**
 * Gets the number of days in the month of the given date.
 * @abstract
 * @param {?} date The date whose month should be checked.
 * @return {?} The number of days in the month of the given date.
 */
getNumDaysInMonth(date: D) {}
/**
 * Clones the given date.
 * @abstract
 * @param {?} date The date to clone
 * @return {?} A new date equal to the given date.
 */
clone(date: D) {}
/**
 * Creates a date with the given year, month, and date. Does not allow over/under-flow of the
 * month and date.
 * @abstract
 * @param {?} year The full year of the date. (e.g. 89 means the year 89, not the year 1989).
 * @param {?} month The month of the date (0-indexed, 0 = January). Must be an integer 0 - 11.
 * @param {?} date The date of month of the date. Must be an integer 1 - length of the given month.
 * @return {?} The new date, or null if invalid.
 */
createDate(year: number, month: number, date: number) {}
/**
 * Gets today's date.
 * @abstract
 * @return {?} Today's date.
 */
today() {}
/**
 * Parses a date from a value.
 * @abstract
 * @param {?} value The value to parse.
 * @param {?} parseFormat The expected format of the value being parsed
 *     (type is implementation-dependent).
 * @return {?} The parsed date, or null if date could not be parsed.
 */
parse(value: any, parseFormat: any) {}
/**
 * Formats a date as a string.
 * @abstract
 * @param {?} date The value to parse.
 * @param {?} displayFormat The format to use to display the date as a string.
 * @return {?} The parsed date, or null if date could not be parsed.
 */
format(date: D, displayFormat: any) {}
/**
 * Adds the given number of years to the date. Years are counted as if flipping 12 pages on the
 * calendar for each year and then finding the closest date in the new month. For example when
 * adding 1 year to Feb 29, 2016, the resulting date will be Feb 28, 2017.
 * @abstract
 * @param {?} date The date to add years to.
 * @param {?} years The number of years to add (may be negative).
 * @return {?} A new date equal to the given one with the specified number of years added.
 */
addCalendarYears(date: D, years: number) {}
/**
 * Adds the given number of months to the date. Months are counted as if flipping a page on the
 * calendar for each month and then finding the closest date in the new month. For example when
 * adding 1 month to Jan 31, 2017, the resulting date will be Feb 28, 2017.
 * @abstract
 * @param {?} date The date to add months to.
 * @param {?} months The number of months to add (may be negative).
 * @return {?} A new date equal to the given one with the specified number of months added.
 */
addCalendarMonths(date: D, months: number) {}
/**
 * Adds the given number of days to the date. Days are counted as if moving one cell on the
 * calendar for each day.
 * @abstract
 * @param {?} date The date to add days to.
 * @param {?} days The number of days to add (may be negative).
 * @return {?} A new date equal to the given one with the specified number of days added.
 */
addCalendarDays(date: D, days: number) {}
/**
 * Gets the RFC 3339 compatible date string (https://tools.ietf.org/html/rfc3339)  for the given
 * date.
 * @abstract
 * @param {?} date The date to get the ISO date string for.
 * @return {?} The ISO date string date string.
 */
getISODateString(date: D) {}
/**
 * Sets the locale used for all dates.
 * @param {?} locale The new locale.
 * @return {?}
 */
setLocale(locale: any) {
    this.locale = locale;
  }
/**
 * Compares two dates.
 * @param {?} first The first date to compare.
 * @param {?} second The second date to compare.
 * @return {?} 0 if the dates are equal, a number less than 0 if the first date is earlier,
 *     a number greater than 0 if the first date is later.
 */
compareDate(first: D, second: D): number {
    return this.getYear(first) - this.getYear(second) ||
        this.getMonth(first) - this.getMonth(second) ||
        this.getDate(first) - this.getDate(second);
  }
/**
 * Checks if two dates are equal.
 * @param {?} first The first date to check.
 * @param {?} second The second date to check.
 *     Null dates are considered equal to other null dates.
 * @return {?}
 */
sameDate(first: D | null, second: D | null): boolean {
    return first && second ? !this.compareDate(first, second) : first == second;
  }
/**
 * Clamp the given date between min and max dates.
 * @param {?} date The date to clamp.
 * @param {?=} min The minimum value to allow. If null or omitted no min is enforced.
 * @param {?=} max The maximum value to allow. If null or omitted no max is enforced.
 * @return {?} `min` if `date` is less than `min`, `max` if date is greater than `max`,
 *     otherwise `date`.
 */
clampDate(date: D, min?: D | null, max?: D | null): D {
    if (min && this.compareDate(date, min) < 0) {
      return min;
    }
    if (max && this.compareDate(date, max) > 0) {
      return max;
    }
    return date;
  }
}

function DateAdapter_tsickle_Closure_declarations() {
/**
 * The locale to use for all dates.
 * @type {?}
 */
DateAdapter.prototype.locale;
}

